import { memo } from "react";
import { cn } from "@repo/design-system/utils";
import { LiveProvenanceBadge } from "@/components/provenance/LiveProvenanceBadge";
import { formatPrice } from "@/utils/money";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import type { StoreSellerCartsSummary } from "./types";

// Product prices already include Australian GST. Rather than rely on the
// cart's backend-computed tax_total (which is 0 unless a tax region is
// configured), derive the GST component directly from the GST-inclusive
// total: for a 10% rate, total = ex-GST * 1.1, so GST = total / 11.
const AU_GST_RATE = 0.1;
const getGstAmount = (grandTotal: number) =>
  grandTotal * (AU_GST_RATE / (1 + AU_GST_RATE));

export interface TotalsBreakdownProps {
  /** Summary containing all totals */
  summary: StoreSellerCartsSummary;
  /** Whether all carts have a shipping method selected */
  allShippingMethodsSelected?: boolean;
  /**
   * Provenance for the rolled-up shipping figure, already collapsed across
   * sellers by the caller (`summary` carries no shipping-specific marker).
   */
  shippingProvenance?: SalonStockerHttpTypes.StoreLiveProvenance;
  /** Optional className */
  className?: string;
}

/**
 * Displays the totals breakdown section of the order summary.
 * Shows subtotal, shipping, discount (if any), and grand total with tax.
 */
export const TotalsBreakdown = memo(function TotalsBreakdown({
  summary,
  allShippingMethodsSelected = false,
  shippingProvenance,
  className
}: TotalsBreakdownProps) {
  const hasDiscount = summary.discount_total > 0;
  const gstAmount = getGstAmount(summary.grand_total);

  // Determine shipping display text:
  // - If no shipping method selected: show "Not selected"
  // - If shipping is 0 and method IS selected: show "Free"
  // - Otherwise: show the formatted price
  const getShippingDisplay = () => {
    if (!allShippingMethodsSelected) {
      return <span className="italic text-gray-500">Not selected</span>;
    }
    if (summary.shipping_total === 0) {
      return "Free";
    }
    return formatPrice(summary.shipping_total);
  };

  return (
    <div className={cn("space-y-3 px-6 py-4", className)}>
      {/* Subtotal */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-600">Subtotal</span>
        <span className="font-medium text-gray-900">
          {formatPrice(summary.item_subtotal)}
        </span>
      </div>

      {/* Shipping */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-600">
          Shipping Total
          {summary.total_sellers > 1 && (
            <span className="text-gray-400">
              {" "}
              ({summary.total_sellers} suppliers)
            </span>
          )}
        </span>
        <span className="flex items-center gap-1.5 font-medium text-gray-900">
          {getShippingDisplay()}
          {/* Only once a rate is actually selected - "Not selected" has no
              provenance to report, and marking it would imply a figure. */}
          {allShippingMethodsSelected && (
            <LiveProvenanceBadge
              provenance={shippingProvenance}
              subject="shipping rate"
              source="your suppliers"
            />
          )}
        </span>
      </div>

      {/* Discount (only if > 0) */}
      {hasDiscount && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Discount</span>
          <span className="font-medium text-green-600">
            -{formatPrice(summary.discount_total)}
          </span>
        </div>
      )}

      {/* Divider */}
      <div className="border-t border-gray-200 pt-3">
        {/* Grand Total Box */}
        <div className="rounded-lg bg-purple-50 p-4">
          <div className="flex items-center justify-between gap-2">
            <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-lg font-semibold text-gray-900">Total</span>
              {/* The one marker that speaks for the whole basket: green only
                  if every supplier's prices and freight were live (KAN-105
                  AC9). One fallback anywhere greys it, which is why it is
                  folded from the carts rather than read off the API's own
                  summary - that one always describes every cart, including
                  the ones a filter has taken off screen. */}
              <LiveProvenanceBadge
                provenance={summary.provenance}
                subject="total"
                source="your suppliers"
                size="md"
                className="flex-shrink-0"
              />
            </span>
            <span className="text-2xl font-bold text-gray-900">
              {formatPrice(summary.grand_total)}
            </span>
          </div>
          {summary.grand_total > 0 && (
            <p className="mt-1 text-right text-sm text-gray-500">
              Includes GST of {formatPrice(gstAmount)}
            </p>
          )}
        </div>
      </div>
    </div>
  );
});
